"use client";

import { Checked } from "@/public/icons/checked";
import { WebShopIcon } from "@/src/components/WebShopIcon";
import useToggle from "@/src/hooks/useToggle";
import { cn } from "@/src/shared/utils/mergeTwClasses";
import { Autocomplete, Box, IconButton, Stack, TextField } from "@mui/material";
import { FieldValues, useController } from "react-hook-form";
import { mulipleTextFieldSxStyles } from "../lib/const";
import type { SelectFieldProps, SelectFieldOptionType } from "../lib/type";
import { has, isArray, isEmpty, isObject, lowerCase } from "lodash";
import { type SyntheticEvent, useMemo, useState } from "react";
import { validationRules } from "@/src/shared/utils/validationRules";
import { safeParseJson } from "@/src/shared/utils/form";
import { isMobile } from "react-device-detect";

export const SelectField = <T extends FieldValues>({
  control,
  name,
  label,
  options,
  excludedOptions = [],
  noOptionsText = "На жаль, за вашим запитом нічого не знайдено",
  loadingText = "Завантажуємо список...",
  placeholder,
  multiple = false,
  hintIcon,
  toggleHintArea,
  closeHintArea,
  openHintArea,
  size,
  required,
  disabled,
  readOnly,
  defaultValue,
  deps,
  textFieldSx,
  onChange: onChangeController,
  onOpen,
  className,
  ...rest
}: SelectFieldProps<T>) => {
  const {
    field: { onChange, value, ref },
    fieldState: { error: fieldError },
  } = useController({
    control,
    name,
    rules: validationRules.SelectInput({ required, disabled, deps }),
    defaultValue: safeParseJson(defaultValue as any),
  });

  const {
    isToggled: isSelectField,
    close: closeSelectField,
    toggle: toggleSelectField,
  } = useToggle({
    initialState: false,
  });

  const [search, setSearch] = useState<string>("");

  const getOptionLabel = (option: SelectFieldOptionType | string) => {
    if (!isObject(option)) {
      const foundOption = options.find(
        (item) => lowerCase(item.value) === lowerCase(option),
      );
      return foundOption ? foundOption.label : option;
    }
    return option.label;
  };

  const getOptionValue = (value: SelectFieldOptionType | string): string => {
    return isObject(value) && has(value, "value")
      ? value.value
      : (value as string);
  };

  const isOptionEqualToValue = (
    option: SelectFieldOptionType,
    value: SelectFieldOptionType | string,
  ) => {
    return lowerCase(option?.value) === lowerCase(getOptionValue(value));
  };

  const parsedValue = useMemo(() => {
    if (multiple && isArray(value)) {
      return value
        .map((val: SelectFieldOptionType | string) => {
          const optionValue = getOptionValue(val);
          const foundOption = options?.find(
            (item) => item.value === optionValue,
          );
          if (foundOption) {
            return foundOption;
          }
          return null;
        })
        .filter(Boolean);
    } else if (value) {
      const optionValue = getOptionValue(value);
      const foundOption = options?.find((item) => item.value === optionValue);
      if (foundOption) {
        return foundOption;
      }
      return value;
    }
    return null;
  }, [value, options, multiple]);

  const selectOptions = useMemo(() => {
    if (isEmpty(excludedOptions)) {
      return options;
    }
    return options?.filter((option) => !excludedOptions.includes(option.value));
  }, [options, excludedOptions, parsedValue]);

  const handleOnOpen = (e: SyntheticEvent) => {
    if (!isSelectField) {
      onOpen?.(e);
    }
    toggleSelectField();
  };

  const withoutKeyboard =
    options.length <= 15 && (rest as any)?.mode !== "source-api-dictionary";

  const skipFilter =
    rest?.mode === "source-api-dictionary" &&
    (rest?.source === "VehicleCity" || rest?.source === "Cities");

  return (
    <Autocomplete
      {...rest}
      disablePortal
      disabled={disabled}
      noOptionsText={
        search
          ? !parsedValue?.value
            ? noOptionsText
            : "Введіть значення для пошуку"
          : "Введіть значення для пошуку"
      }
      open={isSelectField}
      onOpen={handleOnOpen}
      loadingText={loadingText}
      onClose={closeSelectField}
      options={selectOptions}
      multiple={multiple}
      popupIcon={null}
      readOnly={readOnly}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      filterOptions={skipFilter ? (x) => x : undefined}
      size={size}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={!!fieldError}
          placeholder={placeholder}
          helperText={fieldError?.message}
          disabled={readOnly || disabled}
          inputProps={{
            ...params.inputProps,
            readOnly: withoutKeyboard,
            inputMode: withoutKeyboard ? "none" : "text",
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {!(disabled || readOnly) && (
                  <IconButton
                    className={cn("px-2", {
                      // hidden: hintIcon,
                      "max-md:px-0": hintIcon,
                      flex: !hintIcon,
                      "md:flex": true,
                    })}
                    sx={{
                      transform: isSelectField
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOnOpen(e);
                    }}
                  >
                    <WebShopIcon name={"arrowDown"} fill="#009DDE" />
                  </IconButton>
                )}
                {hintIcon && (
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      closeSelectField();
                      toggleHintArea?.();
                    }}
                    className={cn("px-2", {
                      hidden: !hintIcon,
                      flex: hintIcon,
                      "md:hidden": true,
                    })}
                  >
                    <WebShopIcon name={"questionCircle"} />
                  </IconButton>
                )}
              </>
            ),
            disableUnderline: true,
            onChange: (e) => {
              setSearch(e.target.value);
            },
          }}
          onBlur={() => {
            setSearch("");
          }}
          sx={{
            ...(textFieldSx as any),
            ...(multiple ? mulipleTextFieldSxStyles : {}),
          }}
        />
      )}
      renderOption={(props, option, { selected }) => (
        <li
          {...props}
          className={cn("px-1")}
          key={`${option.value}-${option.label}`}
        >
          <Stack
            className={cn(
              props.className,
              "mt-1 justify-between rounded-md bg-white px-2 transition-colors flex-center-row hover:bg-super-light-blue",
              { "bg-super-light-blue": selected },
            )}
          >
            {option.label}
            <Box className={"flex"}>
              {selected && <Checked fill={"var(--blue)"} />}
            </Box>
          </Stack>
        </li>
      )}
      value={parsedValue}
      onChange={(e, value, reason, details) => {
        onChange({ target: { value } });
        if (onChangeController) {
          onChangeController(e, value, reason, details);
        }
      }}
      selectOnFocus={!withoutKeyboard}
      ref={ref}
      className={cn("group", className)}
    />
  );
};
